import type { IOverlayProceduralPattern } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

/**
 * Matemática PURA de los fondos procedurales: copia línea a línea del loop
 * por píxel de `OverlayElementProcedural.tsx` (DOM), sin canvas ni Pixi.
 * La usan el fallback canvas2d del renderer Pixi y los tests de paridad; el
 * shader GPU (`proceduralShaderSource.ts`) replica cada función de acá.
 */

/**
 * Raster de referencia del loop CPU: 192×108 estirado a los bounds del
 * elemento. El shader emula este raster (muestrea en `u = x/W`, `v = y/H`
 * y filtra bilineal) para verse igual que la textura subida por el DOM.
 */
export const PROCEDURAL_RASTER = { width: 192, height: 108 } as const;

/**
 * Índice de patrón para el uniform `uPattern` del shader. Mismo orden en
 * GLSL y WGSL; cualquier patrón desconocido cae en `noise`, como el `else`
 * del DOM.
 */
export const PROCEDURAL_PATTERN_INDEX: Readonly<
  Record<IOverlayProceduralPattern, number>
> = {
  "gradient-flow": 0,
  plasma: 1,
  scanline: 2,
  voronoi: 3,
  noise: 4,
};

export const proceduralPatternIndex = (
  pattern: IOverlayProceduralPattern,
): number => PROCEDURAL_PATTERN_INDEX[pattern] ?? PROCEDURAL_PATTERN_INDEX.noise;

/** Hex `#rrggbb` → `[r,g,b]` 0..255. Tolerante: malformado → negro. */
export const hexToRgb = (hex: string | undefined): [number, number, number] => {
  if (!hex) return [0, 0, 0];
  const m = hex.replace(/^#/, "");
  if (m.length === 3) {
    return [
      parseInt(m[0] + m[0], 16),
      parseInt(m[1] + m[1], 16),
      parseInt(m[2] + m[2], 16),
    ];
  }
  if (m.length === 6) {
    return [
      parseInt(m.slice(0, 2), 16),
      parseInt(m.slice(2, 4), 16),
      parseInt(m.slice(4, 6), 16),
    ];
  }
  return [0, 0, 0];
};

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Value-noise "barato" del DOM: hash entero interpolado con smoothstep.
 * OJO: el segundo producto del hash (`* 1274126177`) se hace en double y
 * excede 2^53, así que JS redondea sus ~8 bits bajos; el shader usa la
 * versión u32 exacta del mismo hash. Es idéntico cuando el producto cabe en
 * 2^53 y, si no, difiere en < 2^-15 (bits bajos del resultado): invisible.
 * Lo único que puede divergir es la siembra `floor(t·100)` cuando `t·100`
 * cae justo en un entero (double vs float32): el hash cambia 30+ veces por
 * segundo, así que es un corrimiento sub-ms del mismo parpadeo.
 */
export const valueNoise = (x: number, y: number, t: number): number => {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const hash = (a: number, b: number) => {
    const n =
      (a * 374761393 + b * 668265263 + Math.floor(t * 100) * 982451653) | 0;
    const mm = (n ^ (n >>> 13)) * 1274126177;
    return ((mm ^ (mm >>> 16)) >>> 0) / 4294967296;
  };
  const a = hash(ix, iy);
  const b = hash(ix + 1, iy);
  const c = hash(ix, iy + 1);
  const d = hash(ix + 1, iy + 1);
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, sx), lerp(c, d, sx), sy);
};

/**
 * Valor 0..1 del patrón en el texel `(u, w)` (u = x/W, w = y/H del raster)
 * al tiempo `time` (segundos × speed) con `scale` ya normalizado (0..2).
 * Es EXACTAMENTE el cuerpo del loop del DOM; `v` alimenta el lerp
 * colorA→colorB y, en `scanline`, también el alpha (`proceduralAlpha`).
 */
export const proceduralPixel = (
  pattern: IOverlayProceduralPattern,
  u: number,
  w: number,
  time: number,
  scale: number,
): number => {
  if (pattern === "gradient-flow") {
    return (
      0.5 +
      0.5 *
        Math.sin((u * 4 + w * 4 + time) * scale + Math.sin(time * 0.7) * 2)
    );
  }
  if (pattern === "plasma") {
    const sx = u * 6 * scale;
    const sy = w * 6 * scale;
    const v =
      (Math.sin(sx + time) +
        Math.sin(sy + time * 0.8) +
        Math.sin((sx + sy) * 0.5 + time * 1.3) +
        Math.sin(Math.sqrt(sx * sx + sy * sy) + time)) /
      4;
    return 0.5 + 0.5 * v;
  }
  if (pattern === "scanline") {
    // Banda luminosa que barre de arriba a abajo (wrap) + rayas finas
    // tenues; v es también el alpha (fuera de la banda queda transparente).
    const pos = (time * 0.12) % 1;
    const d0 = Math.abs(w - pos);
    const d = Math.min(d0, 1 - d0);
    // scale puede ser 0 desde el formulario: piso para no dividir por 0.
    const bandScale = Math.max(0.05, scale);
    const band = Math.exp(-(d * d) / (0.004 * bandScale));
    // Rayas finas moduladas DENTRO de la banda: fuera queda alpha 0.
    const lines = 0.5 + 0.5 * Math.sin(w * 90 * bandScale);
    return band * (0.9 + 0.1 * lines);
  }
  if (pattern === "voronoi") {
    // scale 0 → cellSize = Infinity → ningún seed a distancia finita: v = 0.
    const cellSize = 0.18 / scale;
    const cx = Math.floor(u / cellSize);
    const cy = Math.floor(w / cellSize);
    let minDist = 1;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const nx = cx + dx;
        const ny = cy + dy;
        const seedX =
          (nx + Math.sin(nx * 12.9898 + time * 0.4) * 0.5) * cellSize;
        const seedY =
          (ny + Math.cos(ny * 78.233 + time * 0.4) * 0.5) * cellSize;
        const dd = Math.hypot(u - seedX, w - seedY);
        if (dd < minDist) minDist = dd;
      }
    }
    return Math.min(1, minDist / cellSize);
  }
  // noise (default: cualquier otro valor cae acá, como en el DOM)
  const sx = u * 4 * scale;
  const sy = w * 4 * scale;
  return (
    valueNoise(sx, sy, time * 0.3) * 0.6 +
    valueNoise(sx * 2, sy * 2, time * 0.5) * 0.3 +
    valueNoise(sx * 4, sy * 4, time * 0.7) * 0.1
  );
};

/** Alpha 0..1 del texel: `opacity`, y en `scanline` además el propio `v`. */
export const proceduralAlpha = (
  pattern: IOverlayProceduralPattern,
  v: number,
  opacity: number,
): number => opacity * (pattern === "scanline" ? v : 1);

const TWO_PI = 2 * Math.PI;

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/**
 * Reduce `time` a un período EXACTO del patrón (mismo valor de
 * `proceduralPixel` en double, salvo redondeo) para que el `uTime` float32
 * del shader no le pase argumentos enormes a `sin`/`cos` en emisiones de
 * horas: la precisión de sin en GPU se degrada con |x| grande. Sin período
 * exacto (noise re-siembra por `floor(t·100)`; gradient-flow con `scale·50`
 * no entero) devuelve `time` tal cual: un wrap inexacto sería un salto visible.
 */
export const wrapProceduralTime = (
  pattern: IOverlayProceduralPattern,
  time: number,
  scale: number,
): number => {
  switch (pattern) {
    case "plasma":
      // sin(k·time) con k ∈ {1, 0.8, 1.3}: 10·k entero → período 20π.
      return time % (10 * TWO_PI);
    case "voronoi":
      // sin/cos(… + time·0.4): 0.4·5π = 2π → período 5π.
      return time % (2.5 * TWO_PI);
    case "scanline":
      // (time·0.12) % 1: 0.12·25 = 3 → mismo resto con período 25.
      return time % 25;
    case "gradient-flow": {
      // sin(time·0.7)·2 pide 10·m entero... y (… + time)·scale pide
      // m·scale entero. scale = opts.scale/50: con opts.scale entero k,
      // m = lcm(10, 50/gcd(k,50)) (≤ 50 → período ≤ 100π).
      const k = scale * 50;
      const kInt = Math.round(k);
      if (Math.abs(k - kInt) > 1e-9) return time;
      const mScale = 50 / gcd(kInt, 50);
      const m = (10 * mScale) / gcd(10, mScale);
      return time % (m * TWO_PI);
    }
    default:
      return time;
  }
};
